/**
 * Ponte Comando (Omnibar) -> Telas do sistema.
 *
 * O comando apenas identifica a intenção e prepara um rascunho. Quem valida e grava
 * é sempre a tela oficial do módulo. Este arquivo concentra:
 *  - as chaves de armazenamento temporário (sessionStorage) de cada tipo de rascunho;
 *  - os eventos que avisam a tela de destino quando ela já está aberta;
 *  - o identificador de cada tela de destino no roteador.
 */

export type BridgeKind = 
  | 'costs' 
  | 'financial' 
  | 'services' 
  | 'clients' 
  | 'inventory' 
  | 'appointments' 
  | 'work_orders' 
  | 'recipes'
  | 'pricing';

/** Identificadores das telas de destino, conforme o roteador do AppShell. */
export const BRIDGE_DESTINATIONS: Record<BridgeKind, { default: string; enterprise?: string }> = {
  costs: { default: 'custos', enterprise: 'costs' },
  financial: { default: 'financial_hub', enterprise: 'financial_hub' },
  // Catálogo de Serviços fica dentro do Centro de Custos (aba Serviços)
  services: { default: 'custos', enterprise: 'costs' },
  clients: { default: 'clients', enterprise: 'clients' },
  inventory: { default: 'estoque_hub', enterprise: 'estoque_hub' },
  appointments: { default: 'calendar', enterprise: 'calendar' },
  work_orders: { default: 'work-orders', enterprise: 'work-orders' },
  // Receitas fica dentro do Centro de Custos (aba Receitas)
  recipes: { default: 'custos', enterprise: 'costs' },
  // Precificação Inteligente direciona para o catálogo ou tela correspondente
  pricing: { default: 'catalog', enterprise: 'catalog' }
};

export const BRIDGE_EVENTS: Record<BridgeKind, string> = {
  costs: 'rmax:pending-items-staged',
  financial: 'rmax:financial-draft-staged',
  services: 'rmax:services-staged',
  clients: 'rmax:client-draft-staged',
  inventory: 'rmax:inventory-draft-staged',
  appointments: 'rmax:appointment-draft-staged',
  work_orders: 'rmax:work-order-draft-staged',
  recipes: 'rmax:recipe-draft-staged',
  pricing: 'rmax:pricing-draft-staged'
};

export function getBridgeStorageKey(kind: BridgeKind, userId: string): string {
  if (kind === 'costs') return `r-max:costcenter:${userId}:pending-items`;
  if (kind === 'services') return `r-max:services:${userId}:pending`;
  if (kind === 'financial') return `r-max:financial:${userId}:draft`;
  if (kind === 'clients') return `r-max:clients:${userId}:draft`;
  if (kind === 'inventory') return `r-max:inventory:${userId}:draft`;
  if (kind === 'appointments') return `r-max:appointments:${userId}:draft`;
  if (kind === 'work_orders') return `r-max:work-orders:${userId}:draft`;
  if (kind === 'recipes') return `r-max:recipes:${userId}:draft`;
  if (kind === 'pricing') return `r-max:pricing:${userId}:draft`;
  return `r-max:${kind}:${userId}:draft`;
}

export function resolveBridgeDestination(kind: BridgeKind, isEnterpriseRouter: boolean): string {
  const d = BRIDGE_DESTINATIONS[kind];
  return isEnterpriseRouter && d.enterprise ? d.enterprise : d.default;
}

/* ========================================================================= */
/* 1. FINANCEIRO (CONTAS A PAGAR & RECEBER)                                 */
/* ========================================================================= */

export interface FinancialDraft {
  tipo: 'DESPESA' | 'RECEITA';
  descricao: string;
  valorOriginal: number;
  dataVencimento?: string; // YYYY-MM-DD
  pessoaNome?: string;
  numeroDocumento?: string;
  observacoes?: string;
}

export function stageFinancialDraft(userId: string, draft: FinancialDraft): void {
  sessionStorage.setItem(getBridgeStorageKey('financial', userId), JSON.stringify(draft));
  window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.financial));
}

export function consumeFinancialDraft(userId: string): FinancialDraft | null {
  const key = getBridgeStorageKey('financial', userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as FinancialDraft) : null;
  } catch {
    return null;
  }
}

/* ========================================================================= */
/* 2. CATÁLOGO DE SERVIÇOS                                                  */
/* ========================================================================= */

export interface ServiceDraftItem {
  name: string;
  type: 'fixed' | 'hourly';
  value: number;
  description?: string;
}

export function stageServiceDrafts(userId: string, items: ServiceDraftItem[]): void {
  const key = getBridgeStorageKey('services', userId);
  let existing: ServiceDraftItem[] = [];
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || '[]');
    if (Array.isArray(parsed)) existing = parsed;
  } catch {
    existing = [];
  }
  sessionStorage.setItem(key, JSON.stringify([...existing, ...items]));
  window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.services));
}

export function hasStagedServices(userId: string): boolean {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(getBridgeStorageKey('services', userId)) || '[]');
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

export function consumeServiceDrafts(userId: string): ServiceDraftItem[] {
  const key = getBridgeStorageKey('services', userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/* ========================================================================= */
/* 3. CRM DE CLIENTES                                                       */
/* ========================================================================= */

export interface ClientDraft {
  name: string;
  phone?: string;
  email?: string;
  document?: string;
  address?: string;
  notes?: string;
}

export function stageClientDraft(userId: string, draft: ClientDraft): void {
  sessionStorage.setItem(getBridgeStorageKey('clients', userId), JSON.stringify(draft));
  window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.clients));
}

export function consumeClientDraft(userId: string): ClientDraft | null {
  const key = getBridgeStorageKey('clients', userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ClientDraft) : null;
  } catch {
    return null;
  }
}

/* ========================================================================= */
/* 4. ESTOQUE & LOTES COM VALIDADE                                           */
/* ========================================================================= */

export interface InventoryDraft {
  materialName: string;
  batchCode?: string;
  expirationDate?: string; // YYYY-MM-DD
  quantity: number;
  unitCost?: number;
  notes?: string;
  type?: 'entry_purchase' | 'entry_adjustment' | 'exit_pot_open' | 'exit_freezer_restock' | 'exit_loss_damaged' | 'exit_loss_expired' | 'exit_internal';
  supplierName?: string;
  barcode?: string;
  stockControlMode?: 'pot_open' | 'unit_sale';
}

export function stageInventoryDraft(userId: string, draft: InventoryDraft): void {
  sessionStorage.setItem(getBridgeStorageKey('inventory', userId), JSON.stringify(draft));
  window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.inventory));
}

export function consumeInventoryDraft(userId: string): InventoryDraft | null {
  const key = getBridgeStorageKey('inventory', userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as InventoryDraft) : null;
  } catch {
    return null;
  }
}

/* ========================================================================= */
/* 5. AGENDA & ATENDIMENTOS                                                 */
/* ========================================================================= */

export interface AppointmentDraft {
  clientName: string;
  clientPhone?: string;
  serviceName: string;
  scheduledDate?: string; // YYYY-MM-DD
  scheduledTime?: string; // HH:mm
  price?: number;
  durationMinutes?: number;
  notes?: string;
}

export function stageAppointmentDraft(userId: string, draft: AppointmentDraft): void {
  sessionStorage.setItem(getBridgeStorageKey('appointments', userId), JSON.stringify(draft));
  window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.appointments));
}

export function consumeAppointmentDraft(userId: string): AppointmentDraft | null {
  const key = getBridgeStorageKey('appointments', userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as AppointmentDraft) : null;
  } catch {
    return null;
  }
}

/* ========================================================================= */
/* 6. ORDENS DE SERVIÇO                                                     */
/* ========================================================================= */

export interface WorkOrderDraft {
  clientName: string;
  clientPhone?: string;
  clientAddress?: string;
  clientDocument?: string;
  title: string;
  problemDescription?: string;
  deviceInfo?: string;
  notes?: string;
}

export function stageWorkOrderDraft(userId: string, draft: WorkOrderDraft): void {
  sessionStorage.setItem(getBridgeStorageKey('work_orders', userId), JSON.stringify(draft));
  window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.work_orders));
}

export function consumeWorkOrderDraft(userId: string): WorkOrderDraft | null {
  const key = getBridgeStorageKey('work_orders', userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as WorkOrderDraft) : null;
  } catch {
    return null;
  }
}

/* ========================================================================= */
/* 7. RECEITAS & FICHAS TÉCNICAS                                            */
/* ========================================================================= */

export interface RecipeDraftItem {
  name: string;
  quantity: number;
  unit: string;
  cost?: number;
}

export interface RecipeDraft {
  name: string;
  yieldAmount?: number;
  yieldUnit?: string;
  preparationTimeMinutes?: number;
  items: RecipeDraftItem[];
  instructions?: string;
}

export function stageRecipeDraft(userId: string, draft: RecipeDraft): void {
  sessionStorage.setItem(getBridgeStorageKey('recipes', userId), JSON.stringify(draft));
  window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.recipes));
}

export function hasStagedRecipe(userId: string): boolean {
  try {
    const raw = sessionStorage.getItem(getBridgeStorageKey('recipes', userId));
    return Boolean(raw);
  } catch {
    return false;
  }
}

export function consumeRecipeDraft(userId: string): RecipeDraft | null {
  const key = getBridgeStorageKey('recipes', userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as RecipeDraft) : null;
  } catch {
    return null;
  }
}

/* ========================================================================= */
/* 8. PRECIFICAÇÃO INTELIGENTE (PRODUTOS, SERVIÇOS & KITS)                  */
/* ========================================================================= */

export interface PricingDraft {
  productName?: string;
  acquisitionCost?: number;
  desiredProfitMarginPct?: number;
  sellingPrice?: number;
  daysInStock?: number;
  replacementCost?: number;
  mode?: 'product' | 'service' | 'kit';
  notes?: string;
}

export function stagePricingDraft(userId: string, draft: PricingDraft): void {
  sessionStorage.setItem(getBridgeStorageKey('pricing', userId), JSON.stringify(draft));
  window.dispatchEvent(new CustomEvent(BRIDGE_EVENTS.pricing));
}

export function hasStagedPricing(userId: string): boolean {
  try {
    const raw = sessionStorage.getItem(getBridgeStorageKey('pricing', userId));
    return Boolean(raw);
  } catch {
    return false;
  }
}

export function consumePricingDraft(userId: string): PricingDraft | null {
  const key = getBridgeStorageKey('pricing', userId);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as PricingDraft) : null;
  } catch {
    return null;
  }
}